import { Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { User, Briefcase, FileText, BarChart3, LogOut, Menu, X, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { getUser, clearAuth } from '@/utils/auth';
import { toast } from 'sonner';
import JobSeekerDashboardHome from './JobSeekerDashboardHome';
import ProfileManagement from './ProfileManagement';
import AppliedJobs from './AppliedJobs';

const JobSeekerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'job_seeker') {
      toast.error('Unauthorized access');
      navigate('/job-seeker/login');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    clearAuth();
    toast.success('Logged out successfully');
    navigate('/job-seeker/login');
  };

  const menuItems = [
    { icon: BarChart3, label: 'Dashboard', path: '/job-seeker/dashboard' },
    { icon: User, label: 'My Profile', path: '/job-seeker/profile' },
    { icon: Briefcase, label: 'Applied Jobs', path: '/job-seeker/applied' },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  if (!user) return null;

  const SidebarContent = ({ isMobile = false }) => (
    <>
      <div className={`p-4 border-b border-slate-700`}>
        <div className="flex items-center justify-between">
          {(sidebarOpen || isMobile) && (
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/vvvsi-logo.jpg" alt="VVVSI Logo" className="h-10 w-auto object-contain bg-white rounded p-0.5" />
            </div>
          )}
          {!isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white hover:bg-slate-800"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          )}
        </div>
      </div>

      {/* Role Badge */}
      {(sidebarOpen || isMobile) && (
        <div className="px-4 py-2">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-teal-600 text-white">
            Job Seeker
          </span>
        </div>
      )}

      <nav className="flex-1 p-4 space-y-2">
        {/* Home Link */}
        <Link
          to="/"
          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
          onClick={() => isMobile && setMobileSidebarOpen(false)}
        >
          <Home className="w-5 h-5" />
          {(sidebarOpen || isMobile) && <span>Browse Jobs</span>}
        </Link>
        
        <div className="border-t border-slate-700 my-2"></div>
        
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => isMobile && setMobileSidebarOpen(false)}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
              isActive(item.path) 
                ? 'bg-teal-600 text-white' 
                : 'hover:bg-slate-800 text-slate-300'
            }`}
          >
            <item.icon className="w-5 h-5" />
            {(sidebarOpen || isMobile) && <span>{item.label}</span>}
          </Link>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-700">
        {(sidebarOpen || isMobile) && (
          <div className="mb-3">
            <p className="text-sm text-slate-400">Logged in as</p>
            <p className="font-medium text-white">{user.name || user.mobile}</p>
          </div>
        )}
        <Button
          data-testid="jobseeker-logout-btn"
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start text-white hover:bg-slate-800"
        >
          <LogOut className="w-5 h-5 mr-2" />
          {(sidebarOpen || isMobile) && 'Logout'}
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex ${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 text-white transition-all duration-300 flex-col`}>
        <SidebarContent />
      </aside>

      {/* Mobile Header & Sidebar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 text-white">
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/vvvsi-logo.jpg" alt="VVVSI Logo" className="h-10 w-auto object-contain bg-white rounded p-0.5" />
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-teal-600">
              Job Seeker
            </span>
            <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-slate-800">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-slate-900 text-white border-slate-700">
                <div className="flex flex-col h-full">
                  <SidebarContent isMobile={true} />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <main className="flex-1 overflow-y-auto md:mt-0 mt-16">
        <div className="p-4 md:p-8">
          <Routes>
            <Route path="/" element={<Navigate to="/job-seeker/dashboard" replace />} />
            <Route path="/dashboard" element={<JobSeekerDashboardHome />} />
            <Route path="/profile" element={<ProfileManagement />} />
            <Route path="/applied" element={<AppliedJobs />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default JobSeekerDashboard;
